import { useCallback } from "react";
import { atom, useRecoilState } from "recoil";

/**
 * Filters, tab, sort and pagination of the ShopTier page (`/ShopTier`), kept in Recoil so they
 * survive leaving the page (sidebar menu, a shop's detail page, …) and coming back.
 * Lives in memory only: a full page reload starts from the defaults again.
 */
export interface ShopTierPageState {
  /** Company the state belongs to; a different company (another login) gets fresh defaults. */
  company: string | null;
  selectedTab: string;
  search: string;
  isNavPending: boolean;
  zone: string | null;
  year: string | null;
  action: string | null;
  status: string;
  tierFilter: string | null;
  sortField?: string;
  sortDirection?: "ASC" | "DESC";
  shopListPage: number;
  shopListTake: number;
  auditPage: number;
  auditTake: number;
}

export const SHOP_TIER_PAGE_DEFAULT: ShopTierPageState = {
  company: null,
  selectedTab: "history",
  search: "",
  isNavPending: false,
  zone: null,
  year: null,
  action: null,
  status: "ALL",
  tierFilter: null,
  sortField: undefined,
  sortDirection: undefined,
  shopListPage: 1,
  shopListTake: 10,
  auditPage: 1,
  auditTake: 10,
};

export const shopTierPageState = atom<ShopTierPageState>({
  key: "shopTierPageState",
  default: SHOP_TIER_PAGE_DEFAULT,
});

/**
 * Whole page state plus a `patch` that merges fields. Prefer `patch` when several fields must
 * change together (e.g. a filter and the page numbers it resets) so they land in one update.
 */
export function useShopTierPageState(): [
  ShopTierPageState,
  (patch: Partial<ShopTierPageState> | ((prev: ShopTierPageState) => Partial<ShopTierPageState>)) => void,
] {
  const [state, setState] = useRecoilState(shopTierPageState);
  const patch = useCallback(
    (p: Partial<ShopTierPageState> | ((prev: ShopTierPageState) => Partial<ShopTierPageState>)) =>
      setState((prev) => ({ ...prev, ...(typeof p === "function" ? p(prev) : p) })),
    [setState],
  );
  return [state, patch];
}
